import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { api, ApiError, track } from "@/lib/api";
import { storage, useApp } from "@/store";

const Ctx = createContext(null);

export function useShop() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useShop must be inside ShopProvider");
  return c;
}

/** Live Shopify catalogue + Storefront cart. Cart id persists per user/guest in this browser. */
export function ShopProvider({ children }) {
  const { ready, user, toast, dataEpoch } = useApp();
  const cartKey = `breez_cart_${user?.id ?? "guest"}`;
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [cart, setCart] = useState(null);
  const [cartBusy, setCartBusy] = useState(false);
  const awaitingCheckout = useRef(false);

  const loadCatalog = useCallback(async (refresh = false) => {
    try {
      setCatalogError(null);
      setCatalog(await api(`/shop/catalog${refresh ? "?refresh=true" : ""}`));
    } catch (e) {
      setCatalogError(e.message);
    }
  }, []);

  const saveCart = useCallback(
    async (c) => {
      setCart(c);
      storage.set(cartKey, c?.id ?? null);
    },
    [cartKey],
  );

  useEffect(() => {
    if (dataEpoch) setCart(null);
  }, [dataEpoch]);

  const refreshCart = useCallback(async () => {
    const id = storage.get(cartKey, null);
    if (typeof id !== "string") return setCart(null);
    try {
      setCart(await api(`/cart?id=${encodeURIComponent(id)}`));
    } catch (e) {
      // Completed (checked out) or invalid cart -> start fresh next time.
      if (e instanceof ApiError && e.status === 404) await saveCart(null);
    }
  }, [cartKey, saveCart]);

  useEffect(() => {
    if (!ready) return;
    loadCatalog();
    refreshCart();
  }, [ready, loadCatalog, refreshCart]);

  // Checkout opens in a new tab — refresh the cart when this tab regains focus.
  useEffect(() => {
    const onFocus = () => {
      if (!awaitingCheckout.current || document.visibilityState === "hidden") return;
      awaitingCheckout.current = false;
      refreshCart();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refreshCart]);

  const addToCart = useCallback(
    async (lines) => {
      setCartBusy(true);
      try {
        const c = await api("/cart/lines", { method: "POST", body: { cart_id: cart?.id ?? null, lines } });
        lines.forEach((l) => {
          const p = catalog?.products.find((x) => x.shopify?.variants.some((v) => v.id === l.variant_id));
          const k = catalog?.kits.find((x) => x.variants.some((v) => v.id === l.variant_id));
          track("add_to_cart", { product: p?.slug ?? k?.handle ?? "unknown" });
        });
        await saveCart(c);
        toast(lines.length > 1 ? "Routine added to cart." : "Added to cart.", "success");
        return true;
      } catch (e) {
        toast(e.message, "error");
        return false;
      } finally {
        setCartBusy(false);
      }
    },
    [cart, catalog, saveCart, toast],
  );

  const mutate = useCallback(
    async (path, body) => {
      if (!cart) return;
      setCartBusy(true);
      try {
        await saveCart(await api(path, { method: "POST", body: { cart_id: cart.id, ...body } }));
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) await saveCart(null);
        toast(e.message, "error");
      } finally {
        setCartBusy(false);
      }
    },
    [cart, saveCart, toast],
  );

  const updateLine = useCallback((lineId, quantity) => mutate("/cart/update", { lines: [{ line_id: lineId, quantity }] }), [mutate]);
  const removeLine = useCallback((lineId) => mutate("/cart/remove", { line_ids: [lineId] }), [mutate]);

  const checkout = useCallback(() => {
    if (!cart) return;
    track("checkout_click");
    awaitingCheckout.current = true;
    window.open(cart.checkout_url, "_blank", "noopener,noreferrer");
  }, [cart]);

  const value = useMemo(
    () => ({
      catalog,
      catalogError,
      loadCatalog,
      productBySlug: (slug) => catalog?.products.find((p) => p.slug === slug),
      live: (slug) => catalog?.products.find((p) => p.slug === slug)?.shopify ?? null,
      cart,
      cartBusy,
      addToCart,
      updateLine,
      removeLine,
      refreshCart,
      checkout,
    }),
    [catalog, catalogError, loadCatalog, cart, cartBusy, addToCart, updateLine, removeLine, refreshCart, checkout],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
